/**
 * S-1 Druckversuchsregister (Server: Agent D, Branch `d-s1-druck`, Vertrag
 * `docs/druck/s1-druckversuchsregister.md` Abschnitte 9 und 12): Vor jedem nativen Druck reserviert
 * die Kasse einen Versuch; der Server entscheidet Original oder Kopie, liefert den Bon und bindet
 * den Versuch an Schlüssel, Gerät und Inhalt. Danach meldet die Kasse das Ergebnis.
 *
 * Stand 03.11.2026: Der Server registriert die Routen nur mit `BILANX_NATIVDRUCK=1` und Schema 0095
 * (F1–F5 integriert, golive a6ae310). Die Kassen-App bindet diese Quelle noch nicht ein (F6
 * gesperrt): `druckweg()` bekommt keine Quelle, der Bon läuft weiter über die Hardware-Bridge.
 * Gegen die echte API getestet: `apps/kasse/test/geraet/druckversuch-s1.integration.test.ts`.
 */
import { BONMODELL_FORMAT, pruefeBonmodell } from './bonmodell';
import { SICHER_NICHT_AUSGEFUEHRT, type Bonmodell, type GeraetFehler } from './bridge';

/** Minimaler HTTP-Zugang mit dem Kassenschlüssel der eigenen Kasse (z. B. aus der KassenApi). */
export interface DruckversuchHttp {
  post(pfad: string, body: unknown): Promise<{ status: number; daten: unknown }>;
}

export interface Druckversuch {
  versuchId: string;
  /** Server-Entscheidung; steckt auch in `bon.kopie`. */
  kopie: boolean;
  /** Gleicher Schlüssel, gleiches Gerät, noch offen: dieselbe Antwort erneut. */
  wiederholt: boolean;
  /** SHA-256 hex über `kanonisch(bon)`; geht mit der Meldung zurück. */
  inhaltHash: string;
  bon: Bonmodell;
}

export type Reservierung =
  | { ok: true; versuch: Druckversuch }
  /** Nicht drucken. `code` vom Server (z. B. `versuch_abgeschlossen`) oder vom Adapter. */
  | { ok: false; code: string; text: string };

export type DruckversuchErgebnis = 'gedruckt' | 'unklar' | 'nicht_ausgefuehrt';

export interface Druckmeldung {
  ergebnis: DruckversuchErgebnis;
  code?: string;
  text?: string;
}

export interface DruckversuchQuelle {
  readonly art: 'druckversuch';
  reserviere(
    kassenId: string,
    belegnummer: string,
    opt: { schluessel: string; kopie: boolean },
  ): Promise<Reservierung>;
  /**
   * Wirft nie; eine gescheiterte Meldung lässt den Versuch „reserviert“ (nächster Druck = Kopie).
   * Weist der Server die Meldung als ungültig ab (422), folgt einmal die schlichte Meldung
   * `unklar` ohne Code und Text – nie ein günstigerer Endzustand.
   */
  melde(
    kassenId: string,
    belegnummer: string,
    versuchId: string,
    inhaltHash: string,
    m: Druckmeldung,
  ): Promise<void>;
}

export function druckversuchPfad(kassenId: string, belegnummer: string): string {
  return `/kassen/${encodeURIComponent(kassenId)}/belege/${encodeURIComponent(belegnummer)}/druckversuch`;
}

export function druckmeldungPfad(kassenId: string, belegnummer: string, versuchId: string): string {
  return `${druckversuchPfad(kassenId, belegnummer)}/${encodeURIComponent(versuchId)}/meldung`;
}

/** Kanonische JSON-Darstellung wie `kanonisch` am Server (Schlüssel sortiert, undefined entfällt). */
export function kanonisch(wert: unknown): string {
  if (wert === null || typeof wert !== 'object') {
    return JSON.stringify(wert);
  }
  if (Array.isArray(wert)) {
    return `[${wert.map((w: unknown) => kanonisch(w === undefined ? null : w)).join(',')}]`;
  }
  const o = wert as Record<string, unknown>;
  const teile = Object.keys(o)
    .filter((k) => o[k] !== undefined)
    .sort()
    .map((k) => `${JSON.stringify(k)}:${kanonisch(o[k])}`);
  return `{${teile.join(',')}}`;
}

/** SHA-256 hex über `kanonisch(bon)`; null, wenn WebCrypto fehlt. */
export async function bonHash(bon: Bonmodell): Promise<string | null> {
  const subtle = (globalThis as { crypto?: { subtle?: SubtleCrypto } }).crypto?.subtle;
  if (subtle === undefined) {
    return null;
  }
  const bytes = new TextEncoder().encode(kanonisch(bon));
  const digest = new Uint8Array(await subtle.digest('SHA-256', bytes));
  return Array.from(digest, (b) => b.toString(16).padStart(2, '0')).join('');
}

/** Bonmodell mit sortierten Schlüsseln (gleiche Reihenfolge wie `kanonisch`). */
export function kanonischesBonmodell(bon: Bonmodell): Bonmodell {
  return JSON.parse(kanonisch(bon)) as Bonmodell;
}

const HEX64 = /^[0-9a-f]{64}$/;

function leseBon(roh: unknown): Bonmodell | null {
  if (typeof roh !== 'object' || roh === null) {
    return null;
  }
  const o = roh as Record<string, unknown>;
  const erlaubt = new Set(['format', 'zeilen', 'qrCode', 'kopie']);
  if (Object.keys(o).some((k) => !erlaubt.has(k)) || o.format !== BONMODELL_FORMAT) {
    return null;
  }
  if (!Array.isArray(o.zeilen) || !o.zeilen.every((z) => typeof z === 'string')) {
    return null;
  }
  if (o.qrCode !== undefined && typeof o.qrCode !== 'string') {
    return null;
  }
  if (o.kopie !== undefined && o.kopie !== true) {
    return null;
  }
  const bon: Bonmodell = {
    format: BONMODELL_FORMAT,
    zeilen: o.zeilen,
    ...(o.qrCode === undefined ? {} : { qrCode: o.qrCode }),
    ...(o.kopie === true ? { kopie: true } : {}),
  };
  return pruefeBonmodell(bon) === null ? bon : null;
}

/** Antwort des Servers prüfen; alles Unerwartete heißt: nicht drucken. */
export async function leseDruckversuch(daten: unknown): Promise<Reservierung> {
  const ungueltig = (text: string): Reservierung => ({
    ok: false,
    code: 'antwort_ungueltig',
    text,
  });
  if (typeof daten !== 'object' || daten === null || 'auftragId' in daten) {
    return ungueltig('Server lieferte keinen Druckversuch');
  }
  const o = daten as Record<string, unknown>;
  if (typeof o.versuchId !== 'string' || o.versuchId === '' || typeof o.kopie !== 'boolean') {
    return ungueltig('Druckversuch unvollständig');
  }
  if (typeof o.inhaltHash !== 'string' || !HEX64.test(o.inhaltHash)) {
    return ungueltig('Prüfsumme fehlt');
  }
  const bon = leseBon(o.bon);
  if (bon === null) {
    return ungueltig('Server lieferte keinen druckbaren Bon');
  }
  // Kopie-Entscheidung und Bon müssen übereinstimmen – sonst fehlt „KOPIE“ auf dem Papier
  if (o.kopie !== (bon.kopie === true)) {
    return ungueltig('Kopie-Kennzeichen widersprüchlich');
  }
  const hash = await bonHash(bon);
  if (hash === null) {
    return ungueltig('Prüfsumme nicht berechenbar (WebCrypto fehlt)');
  }
  if (hash !== o.inhaltHash) {
    return ungueltig('Bon passt nicht zur Prüfsumme des Servers');
  }
  return {
    ok: true,
    versuch: {
      versuchId: o.versuchId,
      kopie: o.kopie,
      wiederholt: o.wiederholt === true,
      inhaltHash: o.inhaltHash,
      bon: kanonischesBonmodell(bon),
    },
  };
}

/**
 * Gerätefehler → Meldung. „Sicher nicht ausgeführt“ nur mit Code aus der festen Liste, außer
 * `schluessel_anderer_inhalt`: Unter dem Schlüssel lief am Gerät schon etwas – das melden wir
 * vorsichtig als unklar.
 */
export function meldungAusFehler(f: GeraetFehler): Druckmeldung {
  const text = meldetext(f.text);
  if (!f.unklar && SICHER_NICHT_AUSGEFUEHRT.has(f.code) && f.code !== 'schluessel_anderer_inhalt') {
    return { ergebnis: 'nicht_ausgefuehrt', code: f.code, text };
  }
  return {
    ergebnis: 'unklar',
    ...(/^[a-z0-9_]{1,64}$/.test(f.code) ? { code: f.code } : {}),
    text,
  };
}

/**
 * Freitext so, wie der Server ihn annimmt: höchstens 200 UTF-16-Einheiten, ohne Steuerzeichen
 * (außer Tab/Zeilenumbruch) und ohne einzelne Ersatzhälften; nie mitten in einem Ersatzpaar gekürzt.
 */
export function meldetext(t: string): string {
  let aus = '';
  for (const z of t) {
    const c = z.codePointAt(0) ?? 0;
    const einzelneHaelfte = z.length === 1 && c >= 0xd800 && c <= 0xdfff;
    const steuer = (c < 0x20 && c !== 0x09 && c !== 0x0a && c !== 0x0d) || c === 0x7f;
    const teil = einzelneHaelfte ? '\ufffd' : steuer ? ' ' : z;
    if (aus.length + teil.length > 200) {
      break;
    }
    aus += teil;
  }
  return aus;
}

/** Quelle über die S-1-Routen; [geraetKennung] = N1-Gerätekennung dieses Geräts (für die Kasse registriert). */
export function druckversuchQuelle(
  http: DruckversuchHttp,
  geraetKennung: string,
): DruckversuchQuelle {
  return {
    art: 'druckversuch',
    async reserviere(kassenId, belegnummer, opt) {
      let antwort: { status: number; daten: unknown };
      try {
        antwort = await http.post(druckversuchPfad(kassenId, belegnummer), {
          schluessel: opt.schluessel,
          geraetKennung,
          ...(opt.kopie ? { kopie: true } : {}),
        });
      } catch (e) {
        return { ok: false, code: 'netzfehler', text: e instanceof Error ? e.message : String(e) };
      }
      if (antwort.status === 200 || antwort.status === 201) {
        return leseDruckversuch(antwort.daten);
      }
      const d = (antwort.daten ?? {}) as { code?: unknown; fehler?: unknown };
      return {
        ok: false,
        code: typeof d.code === 'string' ? d.code : `http_${String(antwort.status)}`,
        text:
          typeof d.fehler === 'string' ? d.fehler : `Server antwortet ${String(antwort.status)}`,
      };
    },
    async melde(kassenId, belegnummer, versuchId, inhaltHash, m) {
      const pfad = druckmeldungPfad(kassenId, belegnummer, versuchId);
      try {
        const antwort = await http.post(pfad, {
          ergebnis: m.ergebnis,
          inhaltHash,
          geraetKennung,
          ...(m.code === undefined ? {} : { code: m.code }),
          ...(m.text === undefined ? {} : { text: meldetext(m.text) }),
        });
        if (
          antwort.status === 422 &&
          (m.ergebnis !== 'unklar' || m.code !== undefined || m.text !== undefined)
        ) {
          // Ungültige Meldung (z. B. Code, den der Server nicht kennt): vorsichtig „unklar“
          await http.post(pfad, { ergebnis: 'unklar', inhaltHash, geraetKennung });
        }
      } catch {
        // Versuch bleibt „reserviert“ und sperrt das Original – sicher
      }
    },
  };
}

/** Texte zu den 409-Codes des Servers (Druck unterbleibt immer). */
export function druckversuchAbgelehntText(code: string, text: string): string {
  switch (code) {
    case 'versuch_abgeschlossen':
      return 'Für diesen Beleg lief schon ein Druck – bei Bedarf Nachdruck (trägt „KOPIE“)';
    case 'versuch_anderes_geraet':
      return 'Dieser Druck gehört zu einem anderen Gerät – hier bei Bedarf Nachdruck';
    case 'schluessel_anderer_inhalt':
      return 'Druckauftrag passt nicht zum Beleg – bei Bedarf Nachdruck';
    case 'geraetekennung_unbekannt':
      return 'Gerät ist für diese Kasse nicht eingerichtet';
    default:
      return text;
  }
}
